<template>
  <div class="page-container" v-if="newsData.title">
    <h1 class="page-title">{{ newsData.title }}</h1>
    <p style="text-align: right;">{{ newsData.date }}</p>
    <div class="content">
      <div v-for="(block, index) in contentBlocks" :key="'block-' + index" class="content-section">
        <div class="text-block">
          <h3 v-if="block.type === 'subtitle'" class="content-subtitle" v-html="block.html"></h3>
          <p v-else v-html="block.html"></p>
        </div>
        <div class="content-section image-section" v-if="newsData.other && newsData.other['picture' + (index + 1)]">
          <div class="image-container">
            <img :src="newsData.other['picture' + (index + 1)].url">
            <p class="image-caption" v-if="newsData.other['picture' + (index + 1)].description">{{ newsData.other['picture' + (index + 1)].description }}</p>
          </div>
        </div>
      </div>
    </div>
    
    <div class="back-button">
      <button @click="goBack" class="btn-back">返回新闻列表</button>
    </div>
  </div>
  <div v-else class="page-container">
    <p>新闻内容未找到</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useLangStore } from '../../../pinia/stores/lang';
import zhData from '../../../i18n/zh';
import enData from '../../../i18n/en';

const router = useRouter();
const langStore = useLangStore();

// 根据当前语言选择对应的翻译数据
const translationData = computed(() => {
  return langStore.currentLang.startsWith('zh') ? zhData : enData;
});

// 获取新闻数据
const newsData = computed(() => {
  const newsList = translationData.value.pages.news.company.news.list;
  return newsList.find((item: any) => item.id === 'xinwen3') || {};
});

// 内容块类型定义
type ContentBlock = { html: string; type: 'subtitle' | 'paragraph' };

// 将文本转换为内容块：整段高亮 => 小标题；其余 => 普通段落并保留内联高亮
const makeBlock = (text: string): ContentBlock => {
  const fullHighlight = text.match(/^\s*\[\[highlight\]\](.*?)\[\[\/highlight\]\]\s*$/);
  if (fullHighlight) {
    return { html: fullHighlight[1], type: 'subtitle' };
  }
  const html = text.replace(/\[\[highlight\]\](.*?)\[\[\/highlight\]\]/g, '<span class="highlighted-text">$1</span>');
  return { html, type: 'paragraph' };
};

// 处理内容段落为内容块数组
const contentBlocks = computed<ContentBlock[]>(() => {
  if (!newsData.value.content) return [];
  return newsData.value.content
    .split('---')
    .filter((p: string) => p.trim())
    .map((p: string) => makeBlock(p));
});

const goBack = () => {
  router.push('/news/company');
};
</script>

<style scoped lang="scss">
.page-container {
  overflow: auto;
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  line-height: 1.4;
  text-align: center;
}

.content-section {
  margin-bottom: 20px;
}

.text-block {
  p {
    font-size: 16px;
    line-height: 1.8;
    color: #333;
    margin-bottom: 20px;
    text-align: justify;
    text-indent: 2em;
  }
}

.content-subtitle {
  font-size: 18px;
  font-weight: 600;
  color: #1a56db;
  margin: 16px 0 8px;
  text-align: left;
}

.image-section {
  text-align: center;
}

.image-container {
  display: inline-block;
  margin: 10px 0;
}

.image-container img {
  max-width: 100%;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.image-caption {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
}

.highlighted-text {
  background-color: #fff3cd;
  color: #856404;
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 500;
}

.back-button {
  text-align: center;
  margin-top: 50px;
  
  .btn-back {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 12px 30px;
    border-radius: 6px;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    
    &:hover {
      background-color: #0056b3;
    }
  }
}

@media screen and (max-width: 768px) {
  .page-container {
    padding: 20px 15px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .text-block {
    p {
      font-size: 15px;
      text-indent: 1.5em;
    }
  }
}
</style>